import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  HTMLAttributes,
  MouseEvent,
  ReactNode,
} from 'react';
import { joinCss } from '@/shared/lib/css-utils';
import css from './tab-bar.module.scss';

type TabBarProps = HTMLAttributes<HTMLDivElement> & {
  // Horizontally scroll (instead of clipping) when the tabs exceed the
  // available width — keeps every tab reachable on narrow viewports.
  scrollable?: boolean;
};

export const TabBar = ({
  className,
  children,
  scrollable = false,
  ...restProps
}: TabBarProps) => (
  <div
    className={joinCss(css.bar, scrollable && css.scrollable, className)}
    {...restProps}
  >
    {children}
  </div>
);

type TabCommonProps = {
  active?: boolean;
  icon?: ReactNode;
  // 'sm' is a more compact pill for dense toolbars (opt-in).
  size?: 'default' | 'sm';
  className?: string;
  children?: ReactNode;
};

// Without `href` the Tab stays a `<button>` — zero behaviour change for
// existing callers.
type ButtonTabProps = TabCommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof TabCommonProps> & {
    href?: undefined;
  };

// With `href` the Tab renders a real `<a href>` so the browser offers "Open in
// new tab", middle-click, and Cmd/Ctrl+click. A plain left-click is intercepted
// and delegated to `onClick` (SPA nav); modified/non-primary clicks fall
// through to the browser's native anchor handling.
type AnchorTabProps = TabCommonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof TabCommonProps> & {
    href: string;
  };

type TabProps = ButtonTabProps | AnchorTabProps;

export const Tab = ({
  active = false,
  icon,
  size = 'default',
  className,
  children,
  ...restProps
}: TabProps) => {
  const disabled = Boolean((restProps as { disabled?: boolean }).disabled);
  const cls = joinCss(
    css.tab,
    size === 'sm' && css.tabSm,
    active && css.isActive,
    disabled && css.isDisabled,
    className
  );

  if (restProps.href !== undefined) {
    const { href, onClick, ...anchorProps } =
      restProps as AnchorHTMLAttributes<HTMLAnchorElement>;
    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      event.preventDefault();
      onClick?.(event);
    };
    return (
      <a className={cls} href={href} onClick={handleClick} {...anchorProps}>
        {icon}
        {children}
      </a>
    );
  }

  return (
    <button
      className={cls}
      type="button"
      {...(restProps as ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {icon}
      {children}
    </button>
  );
};
